import { SelectionChange, SelectionModel } from '@angular/cdk/collections';
import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ThemePalette } from '@angular/material/core';
import { RootState } from '@next/core-lib/app';
import {
  DefaultFilterOptions,
  FilterAction,
  FilterConfig,
  FilterOption,
  FiltersConfig,
  FilterTypes,
  FilterComponent,
} from '@next/core-lib/components';
import { ExportActions, ExportColumn, ExportFormat } from '@next/core-lib/export';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { SettingsService, SettingsStorage } from '@next/core-lib/settings';
import { TableColumnTypes, TableConfig, TableComponent } from '@next/core-lib/table';
import { UserActions, UserSelectors } from '@next/core-lib/user';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';

import { PigContextKeys } from '../../models/shared.model';

interface DateRange {
  description?: string;
  endDate: Date | null;
  startDate: Date | null;
}

export enum SettingsKeys {
  brsNumbers = 'brsNumbers',
  dateRange = 'dateRange',
  locations = 'locations',
  search = 'search',
  totals = 'totals',
}

export const settingsContextName = PigContextKeys.CONTEXT;

@Component({
  selector: 'app-page-table-filter',
  templateUrl: './page-table-filter.component.html',
  imports: [FilterComponent, ReactiveFormsModule, TableComponent, TranslatePipe],
  providers: [DatePipe],
})
export class PageTableFilterComponent implements OnInit, OnDestroy {
  @Input() pageTitle: string;
  @Input() searchFieldPlaceholder: string;
  @Input() emptyTitle: string;
  @Input() emptyDescription: string;
  @Input() emptyImage: string;
  @Input() emptyActionIcon: string;
  @Input() emptyActionText: string;
  @Input() emptyActionColor: ThemePalette;
  @Input() filterOnBrs: boolean;
  @Input() showEmptyAction = false;
  @Input() isEmpty = false;

  get tableConfig(): TableConfig {
    return this._tableConfig;
  }
  @Input() set tableConfig(value: TableConfig) {
    this._tableConfig = {
      ...value,
      header: true,
    };
    this.exportColumns = this._tableConfig.columns.map((col) => ({
      id: col.id,
      name: col.label,
    }));
  }

  @Output() selectionChange = new EventEmitter();
  @Output() emptyAction = new EventEmitter();
  @Output() loadDataCallback = new EventEmitter();
  @Input() showTotals = true;
  @Input() showFilters = true;
  @Input() showSearch = true;
  @Input() brsNumbers$: Observable<FilterOption<unknown>[]>;
  @Input() data$: Observable<unknown[]>;
  @Input() filterActions: FilterAction[];
  @Input() exportFilename: string;
  @Input() exportPdfCallback: () => void;
  @Input() filterAndSortFunction: (
    data: unknown[],
    locationIds: number[],
    brsNumbers: string[],
  ) => unknown[];
  filterConfig: FilterConfig<FiltersConfig>;
  _tableConfig: TableConfig;
  _brsNumbers: string[];
  filterControl: FormControl = new FormControl();
  selectionText = '';
  exportColumns: { id: string; name: string }[];
  defaultLocations$ = this.store.select(UserSelectors.selectLocations).pipe(
    take(1),
    map((locations) => locations.map((location) => location.id)),
  );

  customerChangedSubscription: Subscription = this.actions$
    .pipe(ofType(UserActions.customerChanged))
    .subscribe(() => {
      this.loadDataCallback.emit();
    });

  selection: SelectionModel<unknown> = new SelectionModel<unknown>(true, [], true);
  readonly subscriptions: Subscription[] = [] as Subscription[];

  constructor(
    private readonly store: Store<RootState>,
    private readonly datePipe: DatePipe,
    protected actions$: Actions,
    private readonly locale: LocaleService,
    private readonly settingsService: SettingsService,
  ) {}

  ngOnInit() {
    const dataSubscription: Subscription = combineLatest([
      this.data$,
      this.filterControl.valueChanges,
    ])
      .pipe(
        tap(
          ([data, filterValues]: [
            unknown[],
            {
              locations: number[];
              search: string;
              range: DateRange;
              totals: boolean;
              brsNumbers: string[];
            },
          ]) => {
            const dataSource = this.tableConfig.dataSource;
            const locationIds = filterValues?.locations;
            const brsNumbers = filterValues?.brsNumbers;

            dataSource.data = this.filterAndSortFunction
              ? this.filterAndSortFunction(data, locationIds, brsNumbers)
              : data;

            dataSource.filter = filterValues?.search?.trim().toLowerCase().replace(',', '.');

            this.tableConfig = {
              ...this.tableConfig,
              dataSource,
            };
          },
        ),
      )
      .subscribe();

    this.subscriptions.push(dataSubscription);
    this.loadDataCallback.emit();
    this.setSelection();

    this.filterConfig = {
      filters: {
        ...(this.showSearch
          ? {
              search: {
                label: this.searchFieldPlaceholder,
                type: FilterTypes.Text,
              },
            }
          : {}),
        range: {
          type: FilterTypes.Daterange,
          store: {
            contextName: settingsContextName,
            storage: SettingsStorage.PlatformSession,
            key: SettingsKeys.dateRange,
          },
        },
        ...(this.showFilters
          ? {
              locations: {
                type: FilterTypes.MultiSelect,
                options: DefaultFilterOptions.Locations,
                defaultValue: this.defaultLocations$,
                store: {
                  contextName: settingsContextName,
                  storage: SettingsStorage.PlatformSession,
                  key: SettingsKeys.locations,
                },
              },
            }
          : {}),
        ...(this.brsNumbers$
          ? {
              brsNumbers: {
                label: this.locale.translate('page-table-filter.brs-numbers'),
                type: FilterTypes.MultiSelect,
                options: this.brsNumbers$,
              },
            }
          : {}),
        ...(this.showTotals
          ? {
              totals: {
                label: this.locale.translate('page-table-filter.show-totals'),
                type: FilterTypes.Toggle,
                priority: true,
                store: {
                  contextName: settingsContextName,
                  storage: SettingsStorage.PlatformSession,
                  key: SettingsKeys.totals,
                },
              },
            }
          : {}),
      },
    };

    const dateRangeChangesSubscription = this.settingsService
      .getSettingsUpdates({
        contextName: settingsContextName,
        storage: SettingsStorage.PlatformSession,
        key: SettingsKeys.dateRange,
      })
      .subscribe((_) => {
        this.loadDataCallback.emit();
      });
    this.subscriptions.push(dateRangeChangesSubscription);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  export(format: unknown) {
    if (format === 'pdf') {
      this.exportPdfCallback();
    } else {
      this.store.dispatch(
        ExportActions.exportFile({
          data: this.tableConfig.dataSource.filteredData.map((row) =>
            Object.entries(row).reduce((acc: { [key: string]: unknown }, [key, value]) => {
              if (
                !this.exportColumns ||
                this.exportColumns.find((col: ExportColumn) => col.id === key)
              ) {
                acc[key] = value;
              }
              return acc;
            }, {}),
          ),
          format: format as ExportFormat,
          fileName: this.exportFilename,
          columns: this.exportColumns,
        }),
      );
    }
  }

  public formatExportData(rows: unknown) {
    const dateColumns = this.tableConfig.columns.filter(
      (col) => col.type === TableColumnTypes.DATE,
    );
    return (rows as { [key: string]: unknown }[]).map((row) =>
      dateColumns.reduce(
        (acc, col) => ({
          ...acc,
          [col.id]: this.datePipe.transform(row[col.id] as Date, 'shortDate'),
        }),
        row,
      ),
    );
  }

  public setSelection(selection?: SelectionChange<unknown>) {
    if (selection) {
      this.selection = selection.source;
      this.selectionChange.emit(selection);
    }

    this.selectionText = `${this.selection?.selected?.length ?? 0} ${this.locale.translate(
      'page-table-filter.selected',
    )}`;
  }
}
